<template>
  <BaseModal
    :title="$i18next.t('message.ai.dubbings.modal.description')"
    hookSufix="DubbingModal"
    :disableDefaultPadding="true"
    :onCloseHook="onCloseHook"
    :onOpenHook="onOpenHook"
    :persistent="!screenTitle && !initialScreen"
    :hideCloseBtn="true"
    :minHeight="minHeight"
    :modalWidth="modalWidth"
    :fullscreen="isMobileBreakpoint"
  >
    <template #content>
      <v-container class="pa-0 d-none">
        <template v-if="currentModalStage.globalStage === 'generate'">
          <StepStage
            :subStage="currentModalStage.subStage"
            @changeSubStage="updateSubStage"
            :totalSteps="4"
          >
            <template #step_1> </template>
            <template #step_2> </template>
            <template #step_3>
              <p
                class="text-h7 my-8 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t('message.ai.dubbings.modal.auto.step_three.title')
                }}
              </p>
              <div
                class="d-flex flex-column text-center container__content-list"
              >
                <v-row
                  class="mt-5"
                  no-gutters
                  v-if="currentDubVoiceListing.length > 1"
                >
                  <v-col>
                    <v-switch
                      v-model="generateDubbingData.sameVoiceToAll"
                      inset
                      class="v-switch__custom-colorized mt-0 pt-0 ml-1 mb-1"
                      @change="handleSetSameVoiceToAll"
                      hide-details
                    >
                      <template #label>
                        <span
                          class="subtitle-1"
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                          >{{
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_three.sameVoiceSwitchLabel'
                            )
                          }}</span
                        >
                      </template>
                    </v-switch>
                  </v-col>
                </v-row>
                <div class="container__upload-list--modal padded"></div>
              </div>
              <audio
                :src="audioPlayState.audioURL.blobURL"
                ref="audioPlay"
              ></audio>
            </template>
            <template #finalScreen> </template>
          </StepStage>
        </template>
      </v-container>
      <screen-initial-dub-sub-template :type="'Dubbing'" v-if="initialScreen" />
      <upload-dub-sub-template
        :type="'Dubbing'"
        :unusedLangCodes="unusedLangCodes"
        :accept="['.mp3']"
        v-if="uploadScreen"
      />
      <screen-title-dub-sub-template
        :type="'Dubbing'"
        v-if="screenTitle"
        :isBatch="isBatch"
      />
      <select-dubbing-tech-template
        v-if="selectDubbingTech"
        @selectedDubbingTech="handleSelectedDubbingTech"
        :existingSubtitlesImpacted="existingSubtitlesImpacted"
        :videos="selectedVideos"
        :aiworks="aiworks"
        :tech="generateDubbingData.tech"
      />
      <dubbing-best-practices-template
        v-if="dubbingBestPractices"
        :tech="generateDubbingData.tech"
        :existingSubtitlesImpacted="existingSubtitlesImpacted"
      />
      <select-dub-sub-template
        :type="'Dubbing'"
        :options="filteredAiDubbingOptions"
        :videos="selectedVideos"
        :price="generatedDubbingTotalPrice"
        :availableAiVoices="availableAiVoices"
        v-if="selectDubbing"
        :allLanguages="availableTranscriptionLangCodes"
        :alreadySelected="generateDubbingData.selectedItems"
        :tech="generateDubbingData.tech"
        :existingSubtitlesImpacted="existingSubtitlesImpacted"
        :generateDubbingData="generateDubbingData"
        @dubbingOptionsChanged="handleDubbingOptionsChanged"
      />
      <handle-existing-subtitles-template
        v-if="handleExistingSubtitles"
        :languages="existingSubtitlesImpacted"
        :generateDubbingData="generateDubbingData"
      />
      <checkout-ai-template
        :type="'Dubbing'"
        v-if="checkoutAi"
        :videos="selectedVideos"
        :totalPrice="generatedDubbingTotalPrice"
        :itemsToBuy="itemsToBuy"
        :isBatch="isBatch"
        :remainingCredits="creditDubRemainCalc"
        :tech="generateDubbingData.tech"
        :existingSubtitlesImpacted="existingSubtitlesImpacted"
        @generateDubbingBatch="handleGenerateDubbing"
      />
      <generate-ai-template :type="'Dubbing'" v-if="generatedDubbing" />
      <audio :src="audioPlayState.audioURL.blobURL" ref="audioPlay"></audio>
    </template>
  </BaseModal>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import Uppy from '@uppy/core'
import tus from '@uppy/tus'
import i18n from '@/translations/lang'

import store from '@/store'
import Storage from '@/utils/Storage'

import BaseModal from '@/components/BaseModal.vue'

import StepStage from './AddSubtitleModal/StepStage.vue'
import videosApi from '@/services/videosApi'
import aiworkflowApi from '@/services/aiworkflowApi'
import { BACKUP_UPLOADER_URL, ENV_CONFIG } from '@/config/envs'
import Portuguese from '@uppy/locales/lib/pt_BR'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import _ from 'lodash'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import SelectDubSubTemplate from './SelectDubSubTemplate.vue'
import CheckoutAiTemplate from './CheckoutAiTemplate.vue'
import ScreenTitleDubSubTemplate from './ScreenTitleDubSubTemplate.vue'
import ScreenInitialDubSubTemplate from './ScreenInitialDubSubTemplate.vue'
import SelectDubbingTechTemplate from './SelectDubbingTechTemplate.vue'
import DubbingBestPracticesTemplate from './DubbingBestPracticesTemplate.vue'
import HandleExistingSubtitlesTemplate from './HandleExistingSubtitlesTemplate.vue'
import UploadDubSubTemplate from './UploadDubSubTemplate.vue'
import GenerateAiTemplate from './GenerateAiTemplate.vue'
import batchAiGenerationDubbingModal from '@/utils/batchAiGenerationDubbingModal.mixin.js'

export default {
  name: 'AddDubbingModal',
  mixins: [aiWorkflowMixin, formatTimeMixin, batchAiGenerationDubbingModal],
  components: {
    BaseModal,

    StepStage,
    SelectDubSubTemplate,
    CheckoutAiTemplate,
    ScreenTitleDubSubTemplate,
    ScreenInitialDubSubTemplate,
    SelectDubbingTechTemplate,
    DubbingBestPracticesTemplate,
    HandleExistingSubtitlesTemplate,
    UploadDubSubTemplate,
    GenerateAiTemplate,
  },
  data: () => ({
    aiworks: [],
    selectDubbingTech: false,
    dubbingBestPractices: false,
    handleExistingSubtitles: false,
    checkoutAi: false,
    selectDubbing: false,
    generatedDubbing: false,
    uploadScreen: false,
    screenTitle: false,
    initialScreen: true,
    itemsToBuy: [],
    uploadFiles: [],
    uppy: {},
    uppyUpdated: 0,
    uppyCompletedOnes: {
      successfuly: [],
      failed: [],
    },
    generateDubbingDataInitial: {},
    subtitles: [],
    generateDubbingData: {
      dubbingOption: 0,
      dubbingFromVideo: '',
      dubbingFromSubtitle: '',
      dubbingSelectedVoices: ['original'],
      searchAvailableLangsFilter: '',
      selectedItems: [],
      sameVoiceToAll: true,
      context: '',
      tech: '',
      numberOfVoices: 0,
      cloneOriginalVoice: true,
      enableDynamicDuration: true,
      enhanceVoiceQuality: false,
      removeBackgroundMusic: false,
      replaceExistingSubtitles: false,
    },
    voicePaths: [
      'Adam-male-pNInz6obpgDQGcFmaJgB',
      'Antoni-male-ErXwobaYiN019PkySvjV',
      'Arnold-male-VR6AewLTigWG4xSOukaG',
      'Bella-female-EXAVITQu4vr4xnSDxMaL',
      'Charlotte-female-XB0fDUnXU5powFXDhCwa',
      'Clyde-male-2EiwWnXFnvU5JabPnv8n',
      'Dave-male-CYw3kZ02Hs0563khs1Fj',
      'Domi-female-AZnzlk1XvdvUeBnXmlld',
      'Emily-female-LcfcDJNUP1GQjkzn1xUU',
      'Fin-female-D38z5RcWu1voky8WS1ja',
      'Matthew-male-Yko7PKHZNXotIFUBG7I9',
      'Patrick-male-ODq5zmih8GrVes37Dizd',
      'Rachel-female-21m00Tcm4TlvDq8ikWAM',
    ],
    audioPlayState: {
      isPlaying: [],
      isFetchingAudio: [],
      audioBlobUrls: {},
      audioURL: {
        originalURL: '',
        blobURL: '',
      },
    },
    currentModalStage: {
      globalStage: 'initial', // initial, upload, generate
      subStage: 1,
    },
    errors: {},
    loadings: {
      uploadDubbing: false,
      generateDubbing: false,
    },
    videoId: '',
    dubbingAiPrice: 1, // promotional - was 5
    originalLang: null,
    preSelectedLang: null,
    existingSubtitlesImpacted: [],
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      dubbingUploadData: (state) => state.video.dubbingData.upload,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    ...mapGetters('user', ['creditsBalance']),
    modalWidth() {
      return this.initialScreen ? '822px' : this.checkoutAi ? '1020px' : '640px'
    },
    selectedVideos() {
      if (this.isBatch && this.batchDubConfig.length > 1) {
        return this.batchDubConfigComputed
      }
      if (this.video && this.video.id) {
        return [this.video]
      }
      return this.batchDubConfigComputed
    },
    impactedSubtitles() {
      const existing = (this.video.subtitles || []).map(s => s.srclang)
      const impacted = (this.generateDubbingData.selectedItems || []).filter(lang =>
        existing.includes(lang.srclang)
      )
      return impacted
    },
    formatVideoTime() {
      return this.formatTime(this.video.length)
    },
    batchDubConfigComputed() {
      return this.batchDubConfig
    },
    creditDubRemainCalc() {
      return this.creditsBalance - this.generatedDubbingTotalPrice
    },
    currentChosenOption() {
      if (this.generateDubbingData.dubbingOption === 0)
        return this.generateDubbingData.dubbingFromVideo
      return this.generateDubbingData.dubbingFromSubtitle
    },
    minHeight() {
      return this.isMobileBreakpoint ? '100%' : 'auto'
    },
    isSourceItemSelected() {
      return this.generateDubbingData.selectedItems.some((item) =>
        this.checkIfListItemIsSource(item)
      )
    },
    generatedDubbingTotalPrice() {
      if (this.isBatch) {
        return this.batchDubTotalPrice
      }
      const length = this.video.length
      const minutes = Math.floor(length / 60)
      const seconds = Math.floor(length % 60)
      const dubs_to_queue = this.generateDubbingData.selectedItems.length
      if (!dubs_to_queue) return 0
      const credits_per_video = this.applyCreditsCalcRule({ minutes, seconds })
      const final_price = dubs_to_queue * credits_per_video

      return final_price
    },
    availableAiVoices() {
      const aiVoices = []
      this.voicePaths.forEach((item) => {
        const splittedItem = item.split('-')
        const itemLabel = `${splittedItem[0]}`
        aiVoices.push({
          label: itemLabel,
          value: item,
        })
      })
      return [
        {
          label: this.$i18next.t(
            'message.ai.dubbings.modal.auto.step_three.cloneVoice'
          ),
          value: 'original',
        },
        ...aiVoices,
      ]
    },
    hasDuplicatedLangs() {
      const dubbingLabels = this.dubbingUploadData.dubbingListToBeUploaded.map(
        (item) => item.label
      )
      return this.containsDuplicates(dubbingLabels)
    },
    isUploadDubbingBtnDisabled() {
      if (this.currentModalStage.subStage === 1) return true
      if (this.currentModalStage.subStage === 2 && this.hasDuplicatedLangs)
        return true
      if (this.isAnyLoadingRunning) return true
      return false
    },
    availableSubtitleLangsToDub() {
      const hasAvailableDubs =
        this.getUnusedDubbingItemsForSubtitlesGen(this.availableAiLangCodes)
          .length > 0

      return hasAvailableDubs ? this.subtitles : []
    },
    showCardButtons() {
      if (this.currentModalStage.globalStage === 'generate') {
        if (this.currentModalStage.subStage < 4) return true
      } else if (this.currentModalStage.globalStage === 'upload') {
        if (this.currentModalStage.subStage < 4) return true
      } else if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    currentDubVoiceListing() {
      if (this.generateDubbingData.dubbingOption === 0) {
        return this.selectedItemsToBeGenerated
      }
      return this.generateDubbingData.selectedItems
    },
    selectedItemsToBeGenerated() {
      return this.generateDubbingData.selectedItems.filter(
        (item) => !this.checkIfListItemIsSource(item)
      )
    },
    isGenerateDubbingBtnDisabled() {
      let condition = true
      if (this.currentModalStage.subStage === 1) {
        if (
          this.generateDubbingData.dubbingOption === 0 &&
          !this.generateDubbingData.dubbingFromVideo
        )
          condition = false
        if (
          this.generateDubbingData.dubbingOption === 1 &&
          !this.generateDubbingData.dubbingFromSubtitle
        )
          condition = false
        if (!this.unusedDubbingLangCodesAi.length) condition = false
      }
      return this.loadings.generateDubbing || !condition
    },
    isActionButtonsHighlighted() {
      if (
        this.currentModalStage.globalStage === 'generate' &&
        this.currentModalStage.subStage === 2
      )
        return true
      return false
    },
    disableDefaultPadding() {
      if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    checkboxIndeterminateStatus() {
      return (
        this.generateDubbingData.selectedItems.length > 0 &&
        this.generateDubbingData.selectedItems.length <
          this.filteredAiDubbingOptions.length
      )
    },
    isAnyAiDubbingItemSelected() {
      return this.generateDubbingData.selectedItems.length > 0
    },
    filteredAiDubbingOptions() {
      // get all available lang codes to generate, filtered by the search
      const dubbingList = !this.generateDubbingData.dubbingOption
        ? this.unusedDubbingForTranscription
        : this.unusedDubbingLangCodesAi
      let filteredDubbingOptions = dubbingList.filter(({ label }) => {
        return this.checkSearch(label)
      })
      if (this.isBatch) {
        filteredDubbingOptions = this.handleBatchDubbingOptions()
      }
      if (this.originalLang !== null && this.originalLang !== 'auto') {
        filteredDubbingOptions = filteredDubbingOptions.filter(
          (dubbingOption) => {
            return (
              dubbingOption.srclang !== this.originalLang &&
              dubbingOption.srclang !== 'ur'
            )
          }
        )
      }
      // sort results by placing disabled items at the top
      const sortedReturn = filteredDubbingOptions.sort((a, b) => {
        if (
          this.checkIfListItemIsSource(a) &&
          !this.checkIfListItemIsSource(b)
        ) {
          return -1
        } else if (
          !this.checkIfListItemIsSource(a) &&
          this.checkIfListItemIsSource(b)
        ) {
          return 1
        }
        return 0
      })

      return sortedReturn
    },
    unusedLangCodes() {
      const allLangs = this.langCodes
      let usedLangs = new Set()

      if (this.isBatch && Array.isArray(this.batchDubConfig)) {
        this.batchDubConfig.forEach(video => {
          (video.audios || []).forEach(audio => {
            if (audio.lang) usedLangs.add(audio.lang)
          })
        })
      } else {
        usedLangs = new Set((this.video.audios || []).map(audio => audio.lang))
      }

      const originalLang = this.isBatch
        ? this.batchDubConfig[0]?.original_lang
        : this.video.original_lang

      return allLangs.filter(
        lang => !usedLangs.has(lang.srclang) && lang.srclang !== originalLang
      )
    },
    logoConfig() {
      return {
        top: -83,
        left: 295,
        size: 0.5,
        rotation: 0,
      }
    },
    arrowConfig() {
      return {
        top: -50,
        left: 100,
        size: 0.65,
        rotation: 100,
      }
    },
    textConfig() {
      return {
        text: this.$i18next.t('message.ai.aiWorkFlowChargeModal.emptyState'),
        top: 70,
        left: 235,
        size: '1em',
        rotation: 0,
        textAlign: 'center',
        textWidth: '40%',
        hrefText: this.$i18next.t(
          'message.ai.aiWorkFlowChargeModal.addCredits'
        ),
        onClick: this.handleGoChargeCredits,
      }
    },
    hasEnoughCredits() {
      const dubPrice = this.generatedDubbingTotalPrice
      const hasEnoughCredits = this.calcEnoughCredits(dubPrice)
      return !(hasEnoughCredits < 0)
    },
    isStepTwo() {
      return this.currentModalStage.subStage === 2
    },
    isStepThree() {
      return this.currentModalStage.subStage === 3
    },
    isStepFour() {
      return this.currentModalStage.subStage === 4
    },
    nounNumber() {
      if (this.batchAllVideosCount > 1) return 'videos'
      return 'video'
    },
    isUploadState() {
      return this.currentModalStage.globalStage === 'upload'
    },
    hasPendingLipSync() {
      const lipsyncWorks = this.aiworks?.filter(work => 
        work.metadata?.is_lipsync === true && 
        work.video_id === this.videoId
      )
      return lipsyncWorks.length > 0
    },
    lipsyncAudios() {
      if (this.batchDubConfig[0]) {
        const lipsyncWorks = this.aiworks?.filter(work => 
          work.metadata?.is_lipsync === true && 
          work.video_id === this.batchDubConfig[0].id
        )
        return lipsyncWorks.length > 0
      }
      return (this.video.config?.lipsync_info ?? []).filter(item => !item.original).length > 0
    },
  },
  mounted() {},
  watch: {
    open(val) {
      if (!val) this.handleClose()
    },
    subtitles() {
      this.checkDubbingOption()
    },
    generateDubbingData: {
      handler(val) {
        if (val.dubbingFromVideo) {
          this.originalLang = val.dubbingFromVideo
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations('video', [
      'UPDATE_AUDIO_LIST',
      'UPDATE_GENERATING_DUBBING_DATA',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['updateCreditsBalance']),
    activateScreen(componentInstance, screenName) {
      const screens = [
        'selectDubbing',
        'uploadScreen',
        'checkoutAi',
        'initialScreen',
        'screenTitle',
        'generatedDubbing',
        'selectDubbingTech',
        'dubbingBestPractices',
        'handleExistingSubtitles',
      ]
      screens.forEach((screen) => {
        componentInstance[screen] = screen === screenName
      })
    },
    handleDubbingOptionsChanged(options) {
      this.generateDubbingData.numberOfVoices = options.numberOfVoices
      this.generateDubbingData.cloneOriginalVoice = options.cloneOriginalVoice
      this.generateDubbingData.enableDynamicDuration = options.enableDynamicDuration
      this.generateDubbingData.enhanceVoiceQuality = options.enhanceVoiceQuality
      this.generateDubbingData.removeBackgroundMusic = options.removeBackgroundMusic
    },
    handleSelectedDubbingTech(tech) {
      this.generateDubbingData.tech = tech
      this.activateScreen(this, 'dubbingBestPractices')
    },
    async updateSubtitles() {
      const isVideoPage = this.$route.name === 'VideoPage'
      const videoId = this.$route.params.id

      if (!videoId || !isVideoPage) {
        this.subtitles = []
        return
      }

      try {
        const videoInfo = await videosApi.getAVideo(videoId)
        this.subtitles = videoInfo.data.config?.subtitles || []
      } catch (error) {
        console.error('Error on fetching subtitles:', error)
        this.subtitles = []
      }
    },
    getItemCost(item) {
      let videoTime = Math.floor(this.video.length / 60)
      if (!videoTime) videoTime = 1
      const calc = this.getMultiplierAmount(item) * videoTime
      return calc ? calc : 1
    },
    checkDubbingOption() {
      if (this.subtitles.length) {
        this.generateDubbingData.dubbingOption = 1
      } else {
        this.generateDubbingData.dubbingOption = 0
      }
    },
    checkIfDubHasTranslation(srclang) {
      return this.subtitles.some((item) => item.srclang === srclang)
    },
    getMultiplierAmount(item) {
      const additional = this.checkIfDubHasTranslation(item.srclang) ? 0 : 1
      if (this.sourceItem.srclang !== 'auto') {
        if (this.checkIfListItemIsSource(item) && !this.subtitles.length) {
          return 5 + 3 + additional
        }
        return 5 + additional
      }
      if (
        this.generateDubbingData.selectedItems.length &&
        this.generateDubbingData.selectedItems[0].srclang === item.srclang &&
        !this.subtitles.length
      ) {
        return 5 + 3 + additional
      }
      return 5 + additional
    },
    handleSetSameVoiceToAll() {
      if (this.generateDubbingData.sameVoiceToAll) {
        const firstVoice = this.generateDubbingData.dubbingSelectedVoices[0]
        this.generateDubbingData.dubbingSelectedVoices =
          this.generateDubbingData.dubbingSelectedVoices.map(() => firstVoice)
      }
    },
    canShowRowValues(item) {
      let isSourceItemSelected = this.isSourceItemSelected
      if (this.sourceItem.srclang === 'auto') {
        isSourceItemSelected = Boolean(
          this.generateDubbingData.selectedItems.length
        )
      }

      return (
        (this.checkIfListItemIsSource(item) && isSourceItemSelected) ||
        (!this.checkIfListItemIsSource(item) &&
          isSourceItemSelected &&
          this.checkIfItemIsSelected(item))
      )
    },
    checkIfItemIsSelected({ srclang }) {
      return this.generateDubbingData.selectedItems.some(
        (item) => item.srclang === srclang
      )
    },
    async handlePlayCurrentAudio(item) {
      if (!this.$refs.audioPlay.paused) {
        this.$refs.audioPlay.pause()
        this.audioPlayState.isPlaying = []
      }
      this.audioPlayState.isPlaying.push(item.country)
      let audioLang = item.country ?? 'us'
      const newAudioURL = `https://speech-mp3-cdn.pandavideo.com/voices/${item.value}-${audioLang}.mp3`
      if (this.audioPlayState.audioBlobUrls[newAudioURL]) {
        this.audioPlayState.audioURL.originalURL = newAudioURL
        this.audioPlayState.audioURL.blobURL =
          this.audioPlayState.audioBlobUrls[newAudioURL]
      } else if (this.audioPlayState.audioURL.originalURL !== newAudioURL) {
        this.audioPlayState.isFetchingAudio.push(item.country)
        try {
          const mp3Response = await fetch(newAudioURL)
          if (!mp3Response.ok) {
            throw new Error(
              `Erro na requisição: ${mp3Response.status} - ${mp3Response.statusText}`
            )
          }
          const blobMp3Response = await mp3Response.blob()
          const objectURL = URL.createObjectURL(blobMp3Response)

          this.audioPlayState.audioURL.originalURL = newAudioURL
          if (this.audioPlayState.audioURL.blobURL !== objectURL) {
            this.audioPlayState.audioBlobUrls[newAudioURL] = objectURL
            this.audioPlayState.audioURL.blobURL = objectURL
          }
        } catch (error) {
          console.error('Erro ao buscar o áudio:', error)
          this.audioPlayState.isFetchingAudio =
            this.audioPlayState.isFetchingAudio.filter(
              (country) => country !== item.country
            )
        }
      }
      this.$refs.audioPlay.currentTime = 0
      this.$refs.audioPlay.volume = 1
      this.$refs.audioPlay.addEventListener('ended', () => {
        const itemCountryIndex = this.audioPlayState.isPlaying.indexOf(
          item.country
        )
        this.audioPlayState.isPlaying.splice(itemCountryIndex, 1)
      })
      this.$refs.audioPlay.oncanplay = () => {
        const isLoadingIndex = this.audioPlayState.isFetchingAudio.indexOf(
          item.country
        )
        if (isLoadingIndex >= 0)
          this.audioPlayState.isFetchingAudio.splice(isLoadingIndex, 1)
        this.$refs.audioPlay.play()
      }
    },
    handleStopCurrentAudio(item) {
      if (item === undefined) {
        this.audioPlayState.isPlaying = []
      } else {
        const itemCountryIndex = this.audioPlayState.isPlaying.indexOf(
          item.country
        )
        this.audioPlayState.isPlaying.splice(itemCountryIndex, 1)
      }
      this.$refs.audioPlay.pause()
    },
    setUppy() {
      const objTus = this.handleTusObject()
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      const instanceUppy = new Uppy({
        autoProceed: true,
        allowMultipleUploads: true,
        restrictions: {
          allowedFileTypes: ['audio/*'],
        },
        locale: Portuguese,
      }).use(tus, objTus)
      this.uppy = instanceUppy

      this.uppy.on('progress', () => {
        this.uploadFiles = this.uppy?.getFiles()
        if (this.uploadFiles.length) {
          this.dubbingUploadData.dubbingListToBeUploaded =
            this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
              const hasUploadItem = this.uploadFiles.filter((uploadItem) => {
                if (uploadItem.meta.lang === item.srclang) return uploadItem
              })
              if (hasUploadItem.length) {
                this.$set(
                  this.dubbingUploadData.dubbingListToBeUploaded,
                  'progress',
                  hasUploadItem[0].progress
                )
                return { ...item, progress: hasUploadItem[0].progress }
              }
              return item
            })
        }
      })
      this.uppy.on('upload-error', (file, error) => {
        console.error(file, error)
        this.dubbingUploadData.dubbingListToBeUploaded =
          this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
            if (item.srclang === file.meta.lang) {
              return { ...item, error: true }
            }
            return item
          })
      })
      this.uppy.on('file-added', (file) => {
        let storageKeys = Storage.getStorageKeys()
        for (let key of storageKeys) {
          if (key[0].includes('tus::tus-uppy')) {
            Storage.removeItem(key[0])
          }
        }
        const userId = this.$store.getters['session/userSub']
        const refreshToken = this.$store.state.user.refreshToken
        const authorization = 'Bearer ' + Storage.getItem('accessToken')
        this.uppy.setFileMeta(file.id, {
          video_id: this.videoId,
          filetype: 'audio',
          lang: file.meta.lang,
          user_id: userId,
          refresh_token: refreshToken,
          authorization: authorization,
        })
        this.uploadFiles = this.uppy.getFiles()
      })
      this.uppy.on('upload-success', (file) => {
        this.uploadFiles = this.uppy.getFiles()

        this.dubbingUploadData.dubbingListToBeUploaded =
          this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
            if (item.srclang === file.meta.lang) {
              return { ...item, completed: true }
            }
            return item
          })
      })
      this.uppy.on('complete', () => {
        const finalItems =
          this.dubbingUploadData.dubbingListToBeUploaded.filter(
            (item) => item.completed || item.error
          )

        if (finalItems.length === this.uploadFiles.length) {
          const audioFormattedDubbingList =
            this.dubbingUploadData.dubbingListToBeUploaded
              .filter((item) => item.completed)
              .map((item) => {
                return {
                  lang: item.srclang,
                  type: 'UPLOADED',
                  status: 'DRAFT',
                }
              })

          const currentAudios = this.video.audios || this.batchDubConfig[0]?.audios || []

          this.UPDATE_AUDIO_LIST([
            ...currentAudios,
            ...audioFormattedDubbingList,
          ])
          window.db.videos.update(this.videoId, {
            audios: [...currentAudios, ...audioFormattedDubbingList],
          })
          this.loadings.uploadDubbing = false

          if (this.isBatch) {
            this.$root.$emit('updateVideosPage', {
              ...this.batchDubConfig[0],
              audios: [...currentAudios, ...audioFormattedDubbingList],
            })
          }
        }
      })
    },
    setUppyEventsOff() {
      this.uppy.off('file-added')
      this.uppy.off('upload-success')
      this.uppy.off('progress')
      this.uppy.off('restriction-failed')
      this.uppy.off('back-online')
      this.uppy.off('upload-error')
    },
    handleTusObject() {
      let uploaderUrl = `${BACKUP_UPLOADER_URL}/files/`
      if (ENV_CONFIG !== 'production') {
        uploaderUrl = `${BACKUP_UPLOADER_URL}:8443/files/`
      }
      const storageUrl = Storage.getItem('uploaderUrlv3')
      if (storageUrl) {
        uploaderUrl = `${storageUrl}/files/`
        if (ENV_CONFIG !== 'production') {
          uploaderUrl = `${storageUrl}:8443/files/`
        }
      }
      let objTus = {
        id: 'tus',
        endpoint: uploaderUrl,
        resume: false,
        retryDelays: [0, 1000],
        parallelUploads: 5,
        limit: 2,
      }
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      return objTus
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
    isChrome() {
      return !!window.chrome
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
    updateExistingSubtitlesImpacted() {
      if (this.isBatch && Array.isArray(this.batchDubConfig)) {
        const impacted = []
        ;(this.generateDubbingData.selectedItems || []).forEach(lang => {
          this.batchDubConfig.forEach(video => {
            if ((video.subtitles || []).some(sub => sub.srclang === lang.srclang)) {
              impacted.push({
                videoId: video.id,
                videoTitle: video.title,
                srclang: lang.srclang,
                label: lang.label,
                country: lang.srclang,
              })
            }
          })
        })
        this.existingSubtitlesImpacted = impacted
      } else {
        const existing = (this.video.config?.subtitles || []).map(s => s.srclang)
        this.existingSubtitlesImpacted = (this.generateDubbingData.selectedItems || []).filter(lang =>
          existing.includes(lang.srclang)
        )
      }
    },
    async onOpenHook({ videoId, initialState, isBatch, selectedLang, aiworks }) {
      this.aiworks = aiworks
      this.hideCrispButton()
      if (selectedLang) {
        this.generateDubbingData.selectedItems =
          this.filteredAiDubbingOptions.filter(
            (v) => selectedLang.srclang == v.srclang
          )
        this.itemsToBuy = this.generateDubbingData.selectedItems.map(
          (row) => row.label
        )
      } else {
        this.generateDubbingData.selectedItems = []
        this.itemsToBuy = []
      }
      this.$root.$on('addAudio', async (data) => {
        if (!Object.keys(this.uppy).length) return //eslint-disable-line

        let storageKeys = Storage.getStorageKeys()
        for (let key of storageKeys) {
          if (key[0].includes('tus::tus-uppy')) {
            Storage.removeItem(key[0])
          }
        }

        const addedFile = data.file
        const session = await store().dispatch('session/refreshSession')
        const token = session.accessToken.jwtToken
        const userId = this.$store.getters['session/userSub']
        const refreshToken = session.refreshToken.token

        if (token) {
          Storage.setItem('accessToken', session.accessToken.jwtToken)
        }

        const authorization = 'Bearer' + Storage.getItem('accessToken')

        try {
          this.uppy.addFile({
            source: 'file input',
            name: addedFile.name,
            type: addedFile.type,
            data: addedFile,
            meta: {
              video_id: data.video_id,
              filetype: 'audio',
              lang: data.srclang,
              user_id: userId,
              refresh_token: refreshToken,
              authorization: authorization,
            },
          })
        } catch (e) {
          console.log('ERROR UPLOADING AUDIO: ', e)
          if (e.isRestriction && e.message.match(/duplicate/i)) {
            if (!this.isBatch) {
              const fileName = addedFile.name
              const lastCharacters = fileName.substring(fileName.length - 3)
              const regex = /\([0-9]+\)/
              const regexTest = regex.test(lastCharacters)
              let fileNumber = fileName.slice(-2).charAt(0)
              if (regexTest) {
                const newFileNumber = Number(fileNumber) + 1
                Object.defineProperty(addedFile, 'name', {
                  writable: true,
                  value: addedFile.name.slice(0, -3) + ` (${newFileNumber})`,
                })
              } else {
                Object.defineProperty(addedFile, 'name', {
                  writable: true,
                  value: addedFile.name + ' (1)',
                })
              }
              this.$root.$emit('addAudio', data)
            } else {
              this.loadings.uploadDubbing = false
            }
          } else {
            this.loadings.uploadDubbing = false
          }
        }
      })
      if (this.hasPendingLipSync || this.lipsyncAudios) {
        this.initialScreen = false
        this.activateScreen(this, 'selectDubbingTech')
      } else {
        if (initialState) this.activateScreen(this, 'selectDubbing')
        else this.activateScreen(this, 'initialScreen')
      }
      this.$root.$on('playCurrentAudio', (data) => {
        this.handlePlayCurrentAudio(data.item, data.index)
      })
      this.$root.$on('stopCurrentAudio', (data) => {
        this.handleStopCurrentAudio(data)
      })
      this.$root.$on('selectedDubbingItem', (data) => {
        this.generateDubbingData.selectedItems = data
        this.itemsToBuy = data.map((row) => row.label)
        this.updateExistingSubtitlesImpacted()
      })
      this.$root.$on('checkoutDubbingModal', (payload = {}) => {
        this.generateDubbingData.replaceExistingSubtitles = payload.replaceExistingSubtitles
        this.activateScreen(this, 'checkoutAi')
      })
      this.$root.$on('backDubbingModal', () => {
        this.activateScreen(this, 'selectDubbing')
      })
      this.$root.$on('defaultDubbingLanguage', (data) => {
        this.generateDubbingData.dubbingFromVideo = data
      })
      this.$root.$on('selectedVoice', (data) => {
        this.generateDubbingData.dubbingSelectedVoices = [data]

        this.handleSetSameVoiceToAll()
      })
      this.$root.$on('screenTitleDubbing', () => {
        this.activateScreen(this, 'screenTitle')
      })
      this.$root.$on('selectDubbingScreen', () => {
        this.activateScreen(this, 'selectDubbing')
      })
      this.$root.$on('uploadDubbingScreen', () => {
        this.activateScreen(this, 'uploadScreen')
      })
      this.$root.$on('backDubbingInitialScreen', () => {
        this.activateScreen(this, 'initialScreen')
      })
      this.$root.$on('selectDubbingTechScreen', () => {
        this.activateScreen(this, 'selectDubbingTech')
      })
      this.$root.$on('dubbingBestPracticesScreen', () => {
        this.activateScreen(this, 'dubbingBestPractices')
      })
      this.$root.$on('handleExistingSubtitlesScreen', () => {
        this.activateScreen(this, 'handleExistingSubtitles')
      })
      // this.updateCreditsBalance()

      await this.updateSubtitles()
      if (isBatch) {
        this.videoId = this.batchDubConfig[0]?.id
        
        if (this.hasPendingLipSync || this.lipsyncAudios) {
          this.activateScreen(this, 'selectDubbingTech')
        } else {
          this.activateScreen(this, 'initialScreen')
        }
        
        this.$root.$on('generateDubbingBatch', async () => {
          await this.handleGenerateDubbing()
          this.activateScreen(this, 'generatedDubbing')
          this.$root.$off('generateDubbingBatch')
        })
        this.isBatch = isBatch
        this.handleOpenBatchDubGenerationModal()
        return
      } else {
        this.$root.$on('generateDubbing', async () => {
          await this.handlePostDubbing()
          this.activateScreen(this, 'generatedDubbing')
          this.$root.$off('generateDubbing')
        })

        this.generateDubbingData.dubbingSelectedVoices = [
          ...this.availableAiVoices.map((item) => item.value),
        ]
        this.handleSetSameVoiceToAll()
        this.isBatch = false
      }
      this.generateDubbingDataInitial = _.cloneDeep(this.generateDubbingData)
      this.videoId = videoId
      this.open = true
      this.setUppy()

      this.checkDubbingOption()

      if (this.video.original_lang) {
        this.generateDubbingData.dubbingFromVideo = this.video.original_lang
      } else {
        this.generateDubbingData.dubbingFromVideo = 'auto'
      }

      if (this.subtitles.length) {
        this.generateDubbingData.dubbingFromSubtitle = this.subtitles[0].srclang
      }

      if (initialState) {
        this.handleUpdateModalStage({
          globalStage: 'generate',
          subStage: 1,
        })
      }
      if (selectedLang) {
        this.preSelectedLang = selectedLang
      }
    },
    updateSubStage(subStage) {
      this.currentModalStage.subStage = subStage
    },
    async handleGenerateDubbing() {
      await this.handleBatchDubGeneration()
    },
    selectAllItems() {
      let arr = []

      if (
        this.generateDubbingData.selectedItems.length !==
        this.filteredAiDubbingOptions.length
      ) {
        arr = this.filteredAiDubbingOptions
      }

      this.generateDubbingData.selectedItems = arr
    },
    handlePreSelectedLang() {
      const { dubbingOption } = this.generateDubbingData
      const { dubbingFromVideo } = this.generateDubbingData
      const { dubbingFromSubtitle } = this.generateDubbingData
      const dubbingFrom = dubbingOption ? dubbingFromSubtitle : dubbingFromVideo

      const { srclang } = this.preSelectedLang
      if (dubbingFrom !== srclang) {
        this.checkIfSelected(this.preSelectedLang)
        this.checkboxSelection(this.preSelectedLang)
      }
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.generateDubbingData.selectedItems.filter((lookItem) => {
            return lookItem.srclang !== item.srclang
          }),
        ]
        this.generateDubbingData.selectedItems = [...newArr]
      } else {
        this.generateDubbingData.selectedItems.push(item)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.generateDubbingData.selectedItems.find(
        (searchedItem) => {
          return searchedItem.srclang === item.srclang
        }
      )
      if (foundItem) return true
      return false
    },
    checkSearch(label) {
      if (this.generateDubbingData.searchAvailableLangsFilter) {
        return this.areStringsEqualIgnoringCaseAndSymbols(
          this.generateDubbingData.searchAvailableLangsFilter,
          label
        )
      }
      return true
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    handleGoBack() {
      this.clearErrors()
      let jumpAmount = 1
      if (this.currentModalStage.subStage > 1) {
        if (
          this.currentModalStage.globalStage === 'generate' &&
          this.currentModalStage.subStage === 2
        ) {
          this.generateDubbingData.selectedItems = []
        }
        return (this.currentModalStage.subStage -= jumpAmount)
      }
      this.goToInitial()
    },
    getVoiceId(voice) {
      const voiceName = this.getVoiceName(voice)
      const finalVoiceId = `${voiceName}${
        voiceName !== 'original'
          ? '-' + this.currentDubVoiceListing[0].country
          : ''
      }`
      return finalVoiceId
    },

    getVoiceName(voice) {
      if (!voice || typeof voice !== 'string') {
        return 'original'
      }
      const splittedVoiceId = voice.split('-')
      const voiceName = splittedVoiceId[splittedVoiceId.length - 1]
      return voiceName
    },
    async handlePostDubbing() {
      this.loadings.generateDubbing = true
      let finishedResponses = []
      let fromLang = this.generateDubbingData.dubbingFromVideo

      try {
        const enhancedSubtitle = this.subtitles.find(
          (sub) => sub?.ai_model === 'enhanced' || sub?.is_uploaded
        )
        if (this.generateDubbingData.dubbingOption === 0 || !enhancedSubtitle) {
          const voiceId = this.getVoiceId(
            this.generateDubbingData.dubbingSelectedVoices[0]
          )
          const additionalConfig = {
            background_sound_mode: 'ai',
            voice_id: voiceId,
          }
          let selectedItemsSrcLang = this.currentDubVoiceListing

          if (selectedItemsSrcLang.length) {
            selectedItemsSrcLang = selectedItemsSrcLang.map((item, index) => {
              let returnObj = { lang: item.srclang }
              const voiceName = this.getVoiceName(
                this.generateDubbingData.dubbingSelectedVoices[index]
              )
              returnObj.voice_id = voiceName
              return returnObj
            })
            additionalConfig.generate_dubbing = true
          }

          finishedResponses = await aiworkflowApi.createTranscription({
            fromLang,
            videoId: this.videoId,
            ctx: this.generateDubbingData.context,
            additionalLangs: selectedItemsSrcLang,
            additionalConfig,
            isLipSync: this.generateDubbingData.tech === 'heygen',
            numberOfVoices: this.generateDubbingData.numberOfVoices,
            cloneOriginalVoice: this.generateDubbingData.cloneOriginalVoice,
            enableDynamicDuration: this.generateDubbingData.enableDynamicDuration,
            enhanceVoiceQuality: this.generateDubbingData.enhanceVoiceQuality,
            removeBackgroundMusic: this.generateDubbingData.removeBackgroundMusic,
          })
          this.handleVideosIdAiWorkflow(this.videoId)

          const audios = selectedItemsSrcLang.map((item) => ({
            lang: item.lang,
            type: 'GENERATED',
          }))
          await this.updateDbAudio(this.videoId, audios)

          if (
            this.generateDubbingData.dubbingFromVideo &&
            this.generateDubbingData.dubbingFromVideo !== 'auto'
          ) {
            this.UPDATE_VIDEO_ORIGINAL_LANG(
              this.generateDubbingData.dubbingFromVideo
            )
            this.$root.$emit('updateVideoInfo', this.video)
          }
        } else {
          const additionalConfig = {
            background_sound_mode: 'ai',
          }
          let selectedItemsSrcLang = this.currentDubVoiceListing
          if (selectedItemsSrcLang.length) {
            selectedItemsSrcLang = selectedItemsSrcLang.map((item, index) => {
              let returnObj = { lang: item.srclang }
              const voiceName = this.getVoiceName(
                this.generateDubbingData.dubbingSelectedVoices[index]
              )
              returnObj.voice_id = voiceName
              return returnObj
            })
            additionalConfig.generate_dubbing_for_additional_langs = true
          }

          let subtitlesLangList = []
          if (this.isBatch) {
            subtitlesLangList = this.batchSubtitles.map((sub) =>
              typeof sub === 'object' ? sub.srclang : sub
            )
          } else {
            subtitlesLangList = this.subtitles.map((item) => item.srclang)
          }

          // To create dubbings from existing subtitles
          const dubbingsToCreat = selectedItemsSrcLang
            .map((selectedItemSrcLang) => {
              if (subtitlesLangList.includes(selectedItemSrcLang.lang)) {
                return {
                  srclang: this.getPreparedSrcLang(selectedItemSrcLang.lang),
                  voiceId: selectedItemSrcLang.voice_id,
                }
              }
            })
            .filter((item) => item)

          // To create translations and dubbings for languages without subtitles
          const dubbingsToCreateAndTranslate = selectedItemsSrcLang
            .map((selectedItemSrcLang) => {
              if (!subtitlesLangList.includes(selectedItemSrcLang.lang)) {
                return {
                  toLang: selectedItemSrcLang.lang,
                  additionalConfig: {
                    generate_dubbing: true,
                    voice_id: selectedItemSrcLang.voice_id,
                    background_sound_mode: 'ai',
                  },
                }
              }
            })
            .filter((item) => item)

          // Create dubbings for existing subtitles
          if (dubbingsToCreat.length) {
            const response = await aiworkflowApi.createDubbing({
              fromLangs: dubbingsToCreat,
              videoId: this.videoId,
              isLipSync: this.generateDubbingData.tech === 'heygen',
              numberOfVoices: this.generateDubbingData.numberOfVoices,
              cloneOriginalVoice: this.generateDubbingData.cloneOriginalVoice,
              enableDynamicDuration: this.generateDubbingData.enableDynamicDuration,
              enhanceVoiceQuality: this.generateDubbingData.enhanceVoiceQuality,
              removeBackgroundMusic: this.generateDubbingData.removeBackgroundMusic,
              replaceExistingSubtitles: this.generateDubbingData.replaceExistingSubtitles,
            })
            finishedResponses.push(response)

            const audios = dubbingsToCreat.map((item) => ({
              lang: item.srclang,
              type: 'GENERATED',
            }))
            await this.updateDbAudio(this.videoId, audios)
          }

          // Create translations and dubbings for languages without subtitles
          if (dubbingsToCreateAndTranslate.length) {
            const fromLangValue =
              typeof this.generateDubbingData.dubbingFromSubtitle === 'object'
                ? this.generateDubbingData.dubbingFromSubtitle.srclang
                : this.generateDubbingData.dubbingFromSubtitle

            const sourceLang = this.getPreparedSrcLang(fromLangValue)

            const response = await aiworkflowApi.createTranslation({
              fromLang: sourceLang,
              videoId: this.videoId,
              toLangs: dubbingsToCreateAndTranslate,
              isLipSync: this.generateDubbingData.tech === 'heygen',
              numberOfVoices: this.generateDubbingData.numberOfVoices,
              cloneOriginalVoice: this.generateDubbingData.cloneOriginalVoice,
              enableDynamicDuration: this.generateDubbingData.enableDynamicDuration,
              enhanceVoiceQuality: this.generateDubbingData.enhanceVoiceQuality,
              removeBackgroundMusic: this.generateDubbingData.removeBackgroundMusic,
            })
            finishedResponses.push(response)

            const audios = dubbingsToCreateAndTranslate.map((item) => ({
              lang: item.toLang,
              type: 'GENERATED',
            }))
            await this.updateDbAudio(this.videoId, audios)
          }

          this.handleVideosIdAiWorkflow(this.videoId)
        }
        if (!this.isBatch) {
          this.$root.$emit(
            'addAiWorkflowDubbing',
            (finishedResponses[0] || finishedResponses || {}).data
          )
        }
        this.loadings.generateDubbing = false
      } catch (err) {
        console.error('Error on dubbing generation:', err)
        if (this.isBatch) {
          this.currentModalStage.subStage = 4
        } else {
          if (
            err?.response?.data &&
            err?.response?.data?.errMsg &&
            err?.response?.data?.errCode
          ) {
            const badRequestAllowedValues =
              'should be equal to one of the allowed values: en, en-US, pt, auto, pt-BR, es, es-MX, en-UK, it, it-ch, fr, fr-be, fr-ca, fr-lu, fr-ch, de, de-at, de-li, de-lu, de-ch.'
            this.errors.generate = `${err.response.data.errCode}: ${err.response.data.errMsg}`
            if (err.response.data.errMsg.includes(badRequestAllowedValues)) {
              this.errors.generate = `${i18n.t(
                'message.ai.noWordsFound'
              )}: ${i18n.t('message.ai.skippedWarningNotSupported')}`
            }
          } else if (err?.message) {
            this.errors.generate = err.message
          }
          this.loadings.generateDubbing = false
          throw err
        }
      }

      // this.$root.$emit('reloadBalance')
    },
    async updateDbAudio(video_id, audios) {
      try {
        const videoConfig = await window.db.videos
          .where({ id: video_id })
          .first()
        if (!videoConfig) {
          console.error(`Video with ID ${video_id} not found.`)
          return
        }

        const safeAudios = Array.isArray(audios) ? audios : []
        videoConfig.audios = Array.isArray(videoConfig.audios)
          ? videoConfig.audios
          : []

        videoConfig.audios = [
          ...videoConfig.audios.filter(
            (existingAudio) =>
              !safeAudios.some(
                (newAudio) => newAudio.lang === existingAudio.lang
              )
          ),
          ...safeAudios,
        ]

        await window.db.videos.update(video_id, videoConfig)
      } catch (error) {
        console.error(`Error updating audios for video ID ${video_id}:`, error)
      }
    },
    handleClose() {
      this.activateScreen(this, 'initialScreen')
      this.screenTitle = false
      this.uploadScreen = false
      this.$root.$off('generateDubbing')
      this.$root.$emit('closeDubbingModal')
    },
    onCloseHook() {
      this.showCrispButton()
      this.$root.$off('generateDubbing')
      this.$root.$off('generateDubbingBatch')
      this.$root.$off('screenTitleDubbing')
      this.$root.$off('addAudio')
      this.generateDubbingData = _.cloneDeep(this.generateDubbingDataInitial)
      this.preSelectedLang = null
      this.handleResetUppy()
      this.clearErrors()
      this.goToInitial()
    },
    handleResetUppy() {
      this.uploadFiles = []
      this.uppy = {}
    },
    goToGenerateStage() {
      this.handleUpdateModalStage({
        globalStage: 'generate',
        subStage: 1,
      })
    },
    goToUploadStage() {
      this.handleUpdateModalStage({
        globalStage: 'upload',
        subStage: 1,
      })
    },
    goToInitial() {
      this.handleUpdateModalStage()
    },
    handleUpdateModalStage({ globalStage = 'initial', subStage = 1 } = {}) {
      this.currentModalStage.globalStage = globalStage
      this.currentModalStage.subStage = subStage
    },
    handleGoChargeCredits() {
      this.handleClose()
      return this.$router.push({ name: 'Credits', query: { charge: 'true' } })
    },
    calcEnoughCredits(price) {
      return this.creditsBalance - price
    },
    showGeneratingDubbingInfo() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: this.$t('message.ai.dubbings.toastWait'),
        type: 'info',
        timeout: 5000,
      })
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';
</style>

<style lang="scss" scoped>
.v-simple-checkbox {
  i {
    color: var(--v-primary-base) !important;
  }
}
.text-bold {
  font-weight: 500;
  font-size: 16px;
}
.container {
  &__setup-action {
    width: 347px;
    margin: auto;
  }
}
.container__credit-spend {
  width: 400px;
  position: relative;
  left: 0;
  margin: auto;
}
.bg-color {
  background-color: var(--v-newBackground-base) !important;
}
.card-height {
  height: auto !important;
}
.hide-card {
  visibility: hidden;
}
.card-height-step3 {
  max-height: 30px;
}
.btn-width {
  width: 100px;
}
.margin-stp3 {
  margin-top: 11px !important;
}
</style>

<style lang="scss">
.bg-color-icon {
  .v-image__image {
    background-color: var(--v-newBackground-base) !important;
  }
}

@media screen and (max-height: 768px) {
  .v-card__actions--bot-position {
    height: 100%;
    margin-bottom: -3%;
  }

  .credit-spend-col {
    display: flex;
    align-items: center;
  }
}
</style>
